import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Switch, { SwitchVariant } from '@repo/ui/switch'
import { TextType } from '@repo/ui/typography'
import { FieldValues, Path, PathValue, RegisterOptions, useFormContext } from 'react-hook-form'

export type FormInput<T extends FieldValues, P extends Path<T> = Path<T>> = {
  name: P
  label: string
  variant?: TextType
  switchVariant?: SwitchVariant
  color?: TailwindColor
  withBorder?: boolean
  borderClassName?: string
  reverseCheckedValue?: boolean
} & RegisterOptions<T, P>

type Props<T extends FieldValues> = FormInput<T> & {
  readOnly?: boolean
}

const ControlledSwitch = <T extends FieldValues>({
  name,
  label,
  readOnly,
  switchVariant = 'primary',
  color = tailwindColors.grey.DEFAULT,
  borderClassName,
  variant = 'label',
  disabled,
  withBorder = false,
  reverseCheckedValue = false
}: Props<T>) => {
  const {
    watch,
    setValue,
    formState: { isSubmitted }
  } = useFormContext<T>()
  const value = watch(name)
  const checkedValue = reverseCheckedValue ? !value : value

  const onCheckedChange = (checked: boolean) => {
    setValue(name, (reverseCheckedValue ? !checked : checked) as PathValue<T, Path<T>>, {
      shouldValidate: isSubmitted
    })
  }

  return (
    <Switch
      disabled={disabled || readOnly}
      checked={checkedValue}
      onCheckedChange={onCheckedChange}
      name={name}
      label={label}
      labelVariant={variant}
      color={color}
      variant={switchVariant}
      withBorder={withBorder}
      borderClassName={borderClassName}
    />
  )
}

export default ControlledSwitch
